export const CONTACT_ROLES = ['quote', 'invoice', 'planning'];

export const formatSiteAddress = (address) => {
  if (!address) return '';
  const street = [address.street, address.houseNumber || address.house_number]
    .filter(Boolean)
    .join(' ');
  const place = [address.postalCode || address.postal_code, address.city].filter(Boolean).join(' ');
  return [street, place].filter(Boolean).join(', ');
};

export const siteTitle = (site) => {
  if (!site) return '';
  return site.label?.trim() || formatSiteAddress(site.address) || '';
};

export const contactName = (contact) => {
  if (!contact) return '';
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ').trim();
  return name || contact.email || '';
};

export const siteOptions = (sites = [], { emptyLabel = null } = {}) => {
  const options = sites
    .filter((site) => !site.archived_at)
    .map((site) => {
      const address = formatSiteAddress(site.address);
      const label = site.label && address ? `${site.label} · ${address}` : siteTitle(site);
      return { value: site.id, label: label || site.id };
    });
  return emptyLabel ? [{ value: '', label: emptyLabel }, ...options] : options;
};

/**
 * Contacts usable for a given site: customer-wide contacts plus the site's own.
 */
export const contactsForSite = (contacts = [], siteId = null) =>
  contacts.filter(
    (contact) => !contact.archived_at && (!contact.site_id || contact.site_id === siteId)
  );

export const contactOptions = (contacts = [], { siteId = null, emptyLabel = null } = {}) => {
  const options = contactsForSite(contacts, siteId).map((contact) => {
    const name = contactName(contact);
    const label = contact.job_title ? `${name} (${contact.job_title})` : name;
    return { value: contact.id, label: label || contact.id };
  });
  return emptyLabel ? [{ value: '', label: emptyLabel }, ...options] : options;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isMailableContact = (contact) =>
  Boolean(
    contact && !contact.archived_at && EMAIL_PATTERN.test(String(contact.email || '').trim())
  );

/**
 * Mirrors the API's resolveProposalRecipient: the proposal's contact, then a
 * customer-wide contact with the 'quote' role (primary first), then the lead.
 */
export const resolveProposalRecipient = ({ lead = null, contact = null, contacts = [] } = {}) => {
  if (isMailableContact(contact)) return { ...contact, email: contact.email.trim() };
  const quoteContact = contacts
    .filter(isMailableContact)
    .filter((c) => !c.site_id && (c.roles || []).includes('quote'))
    .sort((a, b) => Number(Boolean(b.is_primary)) - Number(Boolean(a.is_primary)))[0];
  if (quoteContact) return { ...quoteContact, email: quoteContact.email.trim() };
  if (!lead?.email) return null;
  return { email: lead.email, first_name: lead.first_name, last_name: lead.last_name };
};

export const countBulkLines = (text = '') =>
  text.split(/\r?\n/).filter((line) => line.trim()).length;

export const isSameAddress = (a, b) => {
  if (!a || !b) return false;
  const norm = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/\s+/g, '');
  return (
    norm(a.street) === norm(b.street) &&
    norm(a.houseNumber || a.house_number) === norm(b.houseNumber || b.house_number) &&
    norm(a.postalCode || a.postal_code) === norm(b.postalCode || b.postal_code)
  );
};
